/**
 * Parser for character breakdown content
 */

export interface CharacterInfo {
  name: string;
  roleType?: string; // Başrol / Yan Rol / Figüran gibi kısa tip
  characterType?: string; // Karakterin Tipi (Rol Tipi ile aynı olabilir)
  shortDescription?: string; // Karakter Kısa Tanıtımı (uzun açıklama, replikler olmadan)
  totalScenes?: string;
  totalReplicas?: string;
  replicas: Array<{
    scene: string;
    text: string;
  }>;
  interactions?: string[];
}

/**
 * Parse character breakdown text into structured character data
 */
export function parseCharacterBreakdown(content: string): CharacterInfo[] {
  if (!content || typeof content !== 'string') return [];

  const characters: CharacterInfo[] = [];
  
  try {
    // Split by character sections (lines starting with number and "KARAKTER ADI:")
    const characterSections = content.split(/(?=\d+\.\s*KARAKTER ADI:)/g).filter(s => s.trim());

    for (const section of characterSections) {
      const character: CharacterInfo = {
        name: '',
        replicas: [],
        interactions: []
      };

      // Extract character name
      const nameMatch = section.match(/(?:\d+\.\s*)?KARAKTER ADI:\s*(.+?)(?:\n|$)/i);
      if (nameMatch) {
        let name = nameMatch[1].trim();
        // Clean markdown from character name
        name = name.replace(/\*\*/g, '').replace(/__/g, '').replace(/\*/g, '').replace(/_/g, '').trim();
        character.name = name;
      }

      // Extract role type (Başrol / Yan Rol / Figüran gibi)
      const roleMatch = section.match(/Rol Tipi:\s*(.+?)(?:\n|$)/i);
      if (roleMatch) {
        let roleType = roleMatch[1].trim();
        // Clean markdown
        roleType = roleType.replace(/\*\*/g, '').replace(/__/g, '').replace(/\*/g, '').replace(/_/g, '').trim();
        character.roleType = roleType;
        character.characterType = roleType; // Karakterin Tipi ile aynı
      }

      // Extract short description (Kısa Tanım) - but stop before "b. Prodüksiyon Özeti" or "c. Replik"
      // This should be the long description without replicas
      const descMatch = section.match(/Kısa Tanım:\s*([\s\S]+?)(?=\n\s*b\.|$)/i);
      if (descMatch) {
        let desc = descMatch[1].trim();
        
        // Remove everything after "Replik" or "Diyalog" keywords (replikleri çıkar)
        const replicaIndex = desc.search(/(?:\n|^).*?(?:Replik|Diyalog|replik|diyalog)/i);
        if (replicaIndex > 0) {
          desc = desc.substring(0, replicaIndex).trim();
        }
        
        // Remove markdown formatting more aggressively
        desc = desc.replace(/\*\*/g, '').replace(/__/g, '').replace(/\*/g, '').replace(/_/g, '');
        desc = desc.replace(/##+/g, '').replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1'); // Remove links
        
        // Clean up multiple newlines and spaces, but preserve paragraph structure
        desc = desc.replace(/\n{3,}/g, '\n\n').replace(/[ \t]{2,}/g, ' ').trim();
        character.shortDescription = desc;
      }

      // Extract total scenes
      const scenesMatch = section.match(/Toplam Sahne Sayısı:\s*(.+?)(?:\n|$)/i);
      if (scenesMatch) {
        let scenes = scenesMatch[1].trim();
        scenes = scenes.replace(/\*\*/g, '').replace(/__/g, '').trim();
        character.totalScenes = scenes;
      }

      // Extract total replicas
      const replicasMatch = section.match(/Toplam Replik Sayısı:\s*(.+?)(?:\n|$)/i);
      if (replicasMatch) {
        let replicas = replicasMatch[1].trim();
        replicas = replicas.replace(/\*\*/g, '').replace(/__/g, '').trim();
        character.totalReplicas = replicas;
      }

      // Extract replicas (dialog lines) - try multiple patterns
      const replicasPatterns = [
        /c\.\s*Replik\s*\(Diyalog\)\s*Dökümü:\s*([\s\S]*?)(?=\n\s*d\.|$)/i,
        /c\.\s*Replik\s*\(Diyalog\)\s*Dökümü[:\s]*([\s\S]*?)(?=\n\s*d\.|$)/i,
        /Replik\s*\(Diyalog\)\s*Dökümü:\s*([\s\S]*?)(?=\n\s*d\.|$)/i,
        /Replikler:\s*([\s\S]*?)(?=\n\s*d\.|$)/i,
        /Replik\s*Dökümü:\s*([\s\S]*?)(?=\n\s*d\.|$)/i,
      ];
      
      let replicasText = '';
      for (const pattern of replicasPatterns) {
        const match = section.match(pattern);
        if (match && match[1]) {
          replicasText = match[1].trim();
          break;
        }
      }
      
      if (replicasText) {
        // Skip if it says "Diyaloğu bulunmuyor" or "Diyalog yok"
        if (!replicasText.toLowerCase().includes('diyaloğu bulunmuyor') && 
            !replicasText.toLowerCase().includes('diyalog yok') &&
            !replicasText.toLowerCase().includes('sessiz karakter') &&
            !replicasText.toLowerCase().includes('replik yok')) {
          
          console.log(`Parsing replicas for ${character.name}, text length: ${replicasText.length}`);
          
          // Match lines like (Sahne X) "Replik text..."
          const lines = replicasText.split('\n').filter(l => l.trim().length > 0);
          
          for (const line of lines) {
            // Skip empty lines or section headers
            if (line.trim().length < 3 || line.match(/^[a-z]\.\s*[A-Z]/)) continue;
            
            // Try to match (Sahne X) "text" format
            const sceneMatch = line.match(/\(Sahne\s*(\d+)\)\s*"([^"]+)"/i);
            if (sceneMatch) {
              character.replicas.push({
                scene: sceneMatch[1].trim(),
                text: sceneMatch[2].trim()
              });
              continue;
            }
            
            // Try to match (Sahne X) text (without quotes)
            const sceneMatch2 = line.match(/\(Sahne\s*(\d+)\)\s*(.+)/i);
            if (sceneMatch2) {
              let text = sceneMatch2[2].trim();
              // Remove quotes if present
              text = text.replace(/^["']|["']$/g, '');
              if (text.length > 0) {
                character.replicas.push({
                  scene: sceneMatch2[1].trim(),
                  text: text
                });
              }
              continue;
            }
            
            // Try to match just quoted text
            const quoteMatch = line.match(/"([^"]+)"/);
            if (quoteMatch) {
              character.replicas.push({
                scene: '',
                text: quoteMatch[1].trim()
              });
              continue;
            }
            
            // Try to match lines starting with numbers (scene numbers)
            const numberedMatch = line.match(/^\s*(\d+)[\.\)]\s*(.+)/);
            if (numberedMatch) {
              let text = numberedMatch[2].trim();
              text = text.replace(/^["']|["']$/g, '');
              if (text.length > 5) { // At least 5 characters to be a valid replica
                character.replicas.push({
                  scene: numberedMatch[1].trim(),
                  text: text
                });
              }
              continue;
            }
            
            // If line starts with quote or looks like a replica, add it
            if (line.trim().startsWith('"') || (line.trim().length > 10 && !line.match(/^[A-Z][^:]*:/))) {
              let cleanText = line.trim().replace(/^["']|["']$/g, '');
              // Clean markdown from replica text
              cleanText = cleanText.replace(/\*\*/g, '').replace(/__/g, '').replace(/\*/g, '').replace(/_/g, '').trim();
              // Skip if it looks like a header or section title
              if (cleanText.length > 5 && !cleanText.match(/^[A-Z][^:]*:$/)) {
                character.replicas.push({
                  scene: '',
                  text: cleanText
                });
              }
            }
          }
          
          console.log(`Found ${character.replicas.length} replicas for ${character.name}`);
        } else {
          console.log(`Skipping replicas for ${character.name} - marked as no dialogue`);
        }
      } else {
        console.log(`No replicas section found for ${character.name}`);
      }

      // Extract interactions - try multiple patterns
      const interactionsPatterns = [
        /d\.\s*Karakter Etkileşim Dökümü[^:]*:\s*([\s\S]*?)(?=\n\s*\d+\.\s*KARAKTER|$)/i,
        /d\.\s*Karakter Etkileşim[^:]*:\s*([\s\S]*?)(?=\n\s*\d+\.\s*KARAKTER|$)/i,
        /Karakter Etkileşim Dökümü[^:]*:\s*([\s\S]*?)(?=\n\s*\d+\.\s*KARAKTER|$)/i,
        /Karakter Etkileşimleri[^:]*:\s*([\s\S]*?)(?=\n\s*\d+\.\s*KARAKTER|$)/i,
        /Karakter Etkileşim[^:]*:\s*([\s\S]*?)(?=\n\s*\d+\.\s*KARAKTER|$)/i,
      ];
      
      let interactionsText = '';
      for (const pattern of interactionsPatterns) {
        const match = section.match(pattern);
        if (match && match[1]) {
          interactionsText = match[1];
          console.log(`Found interactions section for ${character.name} using pattern`);
          break;
        }
      }
      
      if (interactionsText) {
        // Parse interactions - they can be in different formats:
        // 1. **Character Name:** Description
        // 2. Character Name: Description
        // 3. [Character Name]: [X] sahnede birlikte / [Y] karşılıklı diyalog
        const interactionLines: string[] = [];
        const lines = interactionsText.split('\n');
        
        for (const line of lines) {
          let cleaned = line.trim();
          
          // Skip empty lines
          if (cleaned.length === 0) continue;
          
          // Skip section headers
          if (cleaned.match(/^[a-z]\.\s*Karakter/i)) continue;
          if (cleaned.match(/^[a-z]\.\s*[A-Z]/)) continue;
          if (cleaned.match(/^[A-Z][^:]*:\s*$/)) continue;
          
          // Clean markdown
          cleaned = cleaned.replace(/\*\*/g, '').replace(/__/g, '').replace(/\*/g, '').replace(/_/g, '');
          cleaned = cleaned.replace(/##+/g, '').trim();
          
          // If line contains a colon, it's likely an interaction
          if (cleaned.includes(':')) {
            // Format: **Character Name:** Description or Character Name: Description
            const parts = cleaned.split(':');
            if (parts.length >= 2) {
              const charName = parts[0].trim();
              const description = parts.slice(1).join(':').trim();
              
              // Only add if it looks like a valid interaction (has character name and description)
              if (charName.length > 2 && description.length > 3) {
                interactionLines.push(cleaned);
              }
            } else {
              // If no colon but looks like valid text, add it
              if (cleaned.length > 5) {
                interactionLines.push(cleaned);
              }
            }
          } else if (cleaned.length > 5 && !cleaned.match(/^[A-Z\s]+$/)) {
            // Add lines that look like descriptions (not just uppercase headers)
            interactionLines.push(cleaned);
          }
        }
        
        console.log(`Found ${interactionLines.length} interaction lines for ${character.name}`);
        if (interactionLines.length > 0) {
          console.log(`Sample interactions:`, interactionLines.slice(0, 3));
        }
        character.interactions = interactionLines;
      } else {
        console.log(`No interactions section found for ${character.name}`);
      }

      if (character.name) {
        characters.push(character);
      }
    }
  } catch (error) {
    console.error('Error parsing character breakdown:', error);
    return [];
  }

  return characters;
}

